import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import { addJobMenuItem, moveJobMenuToTop } from '../features/jobMenuSlice';
import { pushScreenToStack, moveScreenToTop } from '../features/screenStackSlice';
import { changeOperation } from '../features/operationSlice';

import { getOperationKey, stringsEqualIgnoreCase, stringsStartsWithIgnoreCase } from '../util';
import { openNewPage, NavigationType } from '../util/navigationHelpers';
import constants from '../constants';
import {nowMillisecond} from '../dateUtils';
import useJobMenus from "./useJobMenus";
import {useTypedFastStorage} from "./useFastStorage";
import {focusNextTabable, focusTabableByIndex, getAllTabableElements, getCurrentTabableIndex, getTabableElementByIndex} from "../util/inputUtils";
import _ from 'lodash';
import fastStorage from '../fastStorage';

interface initialSelectedInputId {
  inputId: string,
  isLoading: boolean
};

interface focusOfEachTab {
  tabIndexInStorage: number,
  focusedElementIndexInStorage: number
};

export default function useJobMenu(
  label:any,
  urlOperation:string|null,
  parentUrl:string|null = null,
  closeOnLogout:boolean = false,
  cleanupFnc: () => void = () => { },
  parentUrlsArrayOverride: Array<string> | null = null /* Use special param for better compatibility */,
  initialSelectedInputId: initialSelectedInputId = { inputId: '', isLoading: false },
  noOperationSuffix: boolean = false,
  parentOp: string | null = null,
  jobMode:string|undefined = undefined,
  jobType:string|undefined = undefined,
  addTypeSuffix: boolean = false,
  tabIndexStorage:string = '',
  isSearch: boolean = true,
  ) {
  const history = useHistory();

  const url = `${history.location.pathname}${history.location.search}`;

  const dispatch = useAppDispatch();
  const {
    removeSingleJobMenu,
    showSingleDependantScreenMessage,
    hasSingleOpenedScreenStartingWithUrl,
    hasSingleOpenedScreenStartingWithUrlWithoutMe,
    hasSingleOpenedScreenInAnyOperationStartingWithUrl,
    hasSingleOpenedScreenInAnyOperationStartingWithUrlWithoutMe,
    getSingleOpenedScreenInAnyOperationStartingWithUrl,
    moveToSingleOpenedScreenInAnyOperationStartingWithUrl,
    replaceSingleCurrentJobItemWith,
    replaceSingleCurrentJobItemLabelWith,
    goBackSingle,
    canGoBackSingle,
    addSingleCleanupFunction,
    isFirstRun,
  } = useJobMenus()
  const operationState = useAppSelector(x => x.operation);
  const screenStack = useAppSelector(x => x.screenStack);
  const activeOperation = operationState.operation;

  const operationTabIndex = !_.isEmpty(tabIndexStorage) ? (fastStorage[`${tabIndexStorage}-${url}`] ?? 0) : 0;
  const [focusedElementInStorage, setFocusedElementInStorage] = useTypedFastStorage<focusOfEachTab[]>('focusedElementInStorage', [{ tabIndexInStorage: (operationTabIndex ?? 0), focusedElementIndexInStorage: -1 }]);
  const [, setFocusedEleIndexCurPageInStorage] = useTypedFastStorage<number>('focusedEleIndexCurPageInStorage', focusedElementInStorage?.find(x => x.tabIndexInStorage === operationTabIndex)?.focusedElementIndexInStorage ?? -1);
  const focusedElementIndex = focusedElementInStorage?.find(x => x.tabIndexInStorage === operationTabIndex)?.focusedElementIndexInStorage ?? -1;

  const tabableElements = getAllTabableElements();

  useEffect(() => {
    document.body.addEventListener('focusin', monitorFocusChange);
    document.body.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.removeEventListener('focusin', monitorFocusChange);
      document.body.removeEventListener('keydown', onKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedElementIndex, tabableElements]);

  useEffect(() => {
    const { isLoading, inputId } = initialSelectedInputId;

    if (isLoading) {
      setTimeout(() => {
        const fastStorageKeys = Object.keys(fastStorage);
        const foundKeys = fastStorageKeys.filter(item => item.includes(url));
        if(foundKeys.length === 0) return; //do not execute after tab closed
        let focusIndex = 0;
    
        if (focusedElementIndex >= 0) {
          focusIndex = focusedElementIndex;
        } else if (inputId) {
          const inputElement = document.getElementById(inputId);
          if (inputElement) {
            focusIndex = getCurrentTabableIndex(inputElement);
            if (focusIndex === -1) focusIndex = 0; //inputId Element가 disable
          }
          else {
            if (focusedElementInStorage && !focusedElementInStorage?.some(x => x.tabIndexInStorage === operationTabIndex)) {
              const tempData = _.cloneDeep(focusedElementInStorage);
              tempData.push({ tabIndexInStorage: operationTabIndex, focusedElementIndexInStorage: 0 });
              setFocusedElementInStorage(tempData);
            }
          }
        }

        if (focusIndex !== undefined) {
          if (focusIndex > getAllTabableElements().length) {
            const inputElement = document.getElementById(inputId);
            if (inputElement) {
              focusIndex = getCurrentTabableIndex(inputElement);
            }
          }
        }
        
        const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
        if (getCurrentTabableIndex(document.activeElement) < 0) setFocusedElement(focusIndex, modals.length <= 0);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedInputId.isLoading, operationTabIndex]);

  const operationToApplyChangesFor = !!urlOperation
    ? urlOperation
    : activeOperation;

  const removeJobMenu = () => {
    removeSingleJobMenu(url || '', operationToApplyChangesFor)
  }

  function setFocusedElement(index: number, focusTabable:boolean = true) {
    if (focusedElementInStorage) {
      const indexToUpdate = focusedElementInStorage.findIndex(data => data.tabIndexInStorage === operationTabIndex);

      if (indexToUpdate !== -1) {
        const tempData = _.cloneDeep(focusedElementInStorage);
        tempData[indexToUpdate].focusedElementIndexInStorage = index;
        setFocusedElementInStorage(tempData);
        setFocusedEleIndexCurPageInStorage(index);

        if(focusTabable) focusTabableByIndex(index);
      }
    }
  }
    
  function monitorFocusChange(e: Event) {
    let node: Element = e.target as Element;
    // for (let i = 0; i < 5; ++i) {
    //   if (!node) {
    //     break;
    //   }
    //   const inputElement = node.querySelector('input, textarea');
    //   if (inputElement?.classList?.contains('ifms-tabable-active')) {
    //     setFocusedElement(getCurrentTabableIndex(inputElement));
    //     return;
    //   }
    //   node = node.parentNode as Element;
    // }
    // const currentCellSelections = document.querySelectorAll('.data-cell-selected');
    // if(currentCellSelections) return;
    if (isTableElement(node)) return;

    if (node.classList?.contains('ifms-tabable-active'))
      setFocusedElement(getCurrentTabableIndex(node));
    else {
      if (document.activeElement?.id.includes('lookupaddressdetails'))
        setFocusedElement(focusedElementIndex ?? 0 + 1);
    }
  }

  function isTableElement(element: Element): boolean {
    const tableEles = document.querySelectorAll('div[role="table"]');
    return Array.from(tableEles).some(tableEle => tableEle.contains(element));
  }

  function onKeyDown(e: KeyboardEvent) {
    const tabKey = 9;
    const f5Key = 116;
    const currentNode = (e.target as Element);
    // const currentCellSelections = document.querySelector('.data-cell-selected'); 
    // if (currentCellSelections) return;

    if (e.keyCode === tabKey) {
      const reactModal = document.querySelector('[class^="ReactModal__Content"]');
      if ((!reactModal?.contains(currentNode) && reactModal) || document.querySelector('[id*=lookuptable]')) return false;

      //(!reactModal?.contains(tableRef) && reactModal)

      if (isTableElement(currentNode)) return;

      const callByLookupAddressDetails = currentNode.id.includes('lookupaddressdetails');
      if (callByLookupAddressDetails) return;
      if (focusedElementIndex === null || focusedElementIndex === -1) return;

      if (e.shiftKey) {
        handleShiftTabKey(e, focusedElementIndex);
      } else {
        handleTabKey(e, focusedElementIndex);
      }
    }
    if (e.keyCode === f5Key && !isSearch) {
      e.returnValue = false;
    }
  }

  function handleShiftTabKey(e: KeyboardEvent, index: number) {
    if (index === null || index === -1) return;

    const currentNode = (e.target as Element);
    const tabableElementLastIndex = tabableElements.length - 1;

    let newIndex = (index === 0) ? tabableElementLastIndex : focusedElementIndex - 1;
    // if (tabableElements.item(newIndex).id === 'table-top-addline-button')
    //   return;

    if (newIndex === tabableElementLastIndex && tabableElements.item(tabableElementLastIndex)?.id.includes('lookupaddressdetails'))
      newIndex = tabableElementLastIndex - 1;
    else {
      const prevElementClassName = getTabableElementByIndex(newIndex).className;
      if (prevElementClassName.includes('-hideforfocus-')) {
        const tableId = prevElementClassName.split('-hideforfocus-')[0];
        const tableElement = document.getElementById(tableId);
        if (tableElement) {
          (currentNode as HTMLElement)?.blur();
          tableElement.focus();
        
          const shiftTabKeyEvent = new KeyboardEvent('keydown', {
            key: 'Tab',
            code: 'Tab',
            keyCode: 9,
            which: 9,
            shiftKey: true, 
            bubbles: true,
            cancelable: true
          });
        
          tableElement.dispatchEvent(shiftTabKeyEvent);
          e.preventDefault();
        }
      }

    }

    setdFocusElement(newIndex, e);
  }

  function handleTabKey(e: KeyboardEvent, index: number) {
    if (index === null || index === -1) return;
    const currentNode = (e.target as Element);
    
    if (document.activeElement?.id.includes('lookupaddressdetails')) return;

    const nextTabableElementClassName = focusNextTabable(currentNode as HTMLInputElement, true)?.className;
    if (nextTabableElementClassName && nextTabableElementClassName.includes('-hideforfocus-')) {
      const tableId = nextTabableElementClassName.split('-hideforfocus-')[0];
      const tableElement = document.getElementById(tableId);

      if (tableElement) {
        (currentNode as HTMLElement)?.blur();
        tableElement.focus();
        
        const tabKeyEvent = new KeyboardEvent('keydown', {
          key: 'Tab',
          code: 'Tab',
          keyCode: 9,
          which: 9,
          bubbles: true,
          cancelable: true
        });

        tableElement.dispatchEvent(tabKeyEvent);
        e.preventDefault();
      }
      return;
    }

    const newIndex = (tabableElements.length - 1 === index) ? 0 : index + 1;
    setdFocusElement(newIndex, e);
  }
  
  function setdFocusElement(index: number, e: KeyboardEvent) {
    setFocusedElement(index);
    e.preventDefault();
    e.stopPropagation();
  }

  function hasOpenedScreenStartingWithUrl(queryUrl:string) {
    return hasSingleOpenedScreenStartingWithUrl(urlOperation || '', queryUrl)
  }

  function hasOpenedScreenStartingWithUrlWithoutMe(queryUrl:string, ignoreUrl:string) {
    return hasSingleOpenedScreenStartingWithUrlWithoutMe(urlOperation || '', queryUrl, ignoreUrl)
  }

  function hasOpenedScreenInAnyOperationStartingWithUrl(queryUrl:string) {
    return hasSingleOpenedScreenInAnyOperationStartingWithUrl(queryUrl);
  }

  function hasOpenedScreenInAnyOperationStartingWithUrlWithoutMe(queryUrl:string, ignoreUrl:string) {
    return hasSingleOpenedScreenInAnyOperationStartingWithUrlWithoutMe(queryUrl, ignoreUrl);
  }

  function getOpenedScreenInAnyOperationStartingWithUrl(queryUrl:string) {
    return getSingleOpenedScreenInAnyOperationStartingWithUrl(queryUrl);
  }

  async function showDependantScreenMessage(title:React.ReactNode|string|null|undefined) {
    return showSingleDependantScreenMessage(url, urlOperation || '', title)
  }

  async function canGoBack(): Promise<boolean> {
    return canGoBackSingle(urlOperation || '');
  }

  async function goBack(cb:(() => void)|null = null, backScreenOverride:string|null = null):Promise<void> {
    return goBackSingle(url, urlOperation || '', cb, backScreenOverride);
  }

  function replaceCurrentJobItemWith(newUrl:string, newLabel:any) {
    replaceSingleCurrentJobItemWith(newUrl, newLabel, label, url)
  }

  function replaceCurrentJobItemLabelWith(newLabel:string|null) {
    replaceSingleCurrentJobItemLabelWith(newLabel, label, url)
  }


  function moveToOpenedScreenInAnyOperationStartingWithUrl(oepnRequestUrl:string, queryUrls:string[], needAfterScreenUrl?: boolean|undefined){
    moveToSingleOpenedScreenInAnyOperationStartingWithUrl(oepnRequestUrl, queryUrls, needAfterScreenUrl)
  }

  function getFocusElementInStorage(){
    return focusedElementInStorage;
  }

  useEffect(() => {
    addSingleCleanupFunction(cleanupFnc, url)

    const operation = getOperationKey(operationToApplyChangesFor);
    if (!operation) {
      return;
    }

    if (operation) {
      // Check if parent url is opened
      if (!!parentUrlsArrayOverride) {
        const parentIsOpened = parentUrlsArrayOverride.some(p => {
          // Implementation note: ~ means that screen starts with. For example we can check if there is edit screen opened without knowing exact id
          if (p.startsWith('~')) {
            return !!screenStack[operation].find(x => stringsStartsWithIgnoreCase(x.url, p.substring(1)));
          } else {
            return !!screenStack[operation].find(x => stringsEqualIgnoreCase(x.url, p));
          }
        });
        if (!parentIsOpened) {
          openNewPage(history, '/invalidOperation', NavigationType.newPage);
          return;
        }
      }
      else if (!!parentUrl) {
        const parentIsOpened = !!screenStack[operation].find(x => stringsEqualIgnoreCase(x.url, parentUrl));
        if (!parentIsOpened) {
          openNewPage(history, '/invalidOperation', NavigationType.newPage);
          return;
        }
      }
    }

    const locationState = history.location?.state as any;
    const urlOperationKey = urlOperation ? getOperationKey(urlOperation) : null;

    if (urlOperation === constants.operations.standalone) {
      dispatch(moveJobMenuToTop({
        url,
        operation: getOperationKey(urlOperation) ?? '',
      }));
    }

    if(parentOp) urlOperation = parentOp;

    if (!!urlOperation && urlOperation !== activeOperation) {
      dispatch(moveScreenToTop({
        url,
        operation: getOperationKey(urlOperation) ?? '',
      }));
      dispatch(changeOperation(urlOperation));
    }

    dispatch(addJobMenuItem({
      url,
      label,
      parentUrl,
      operation: urlOperationKey,
      closeOnLogout,
      activeOperation: operation,
      metadata: locationState?.metadata,
      timestamp: nowMillisecond(),
      noOperationSuffix,
      afterScreenUrl: locationState?.afterScreenUrl ?? null,
      jobMode:jobMode,
      jobType:jobType,
      addTypeSuffix,
    }));

    dispatch(pushScreenToStack({
      url,
      parentUrl,
      operation: urlOperationKey,
      activeOperation: operation,
      metadata: locationState?.metadata,
      afterScreenUrl: locationState?.afterScreenUrl ?? null,
    }));
    // setTimeout(() => {
    //   if(focusedElementIndex && focusedElementIndex >= 0) {
    //     focusTabableByIndex(focusedElementIndex)
    //   } else if(initialSelectedInputId) {
    //     focusTabableByIndex(getCurrentTabableIndex(document.getElementById(initialSelectedInputId)))
    //   }
    // }, 500)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // return [goBack, removeJobMenu, showDependantScreenMessage, hasOpenedScreenStartingWithUrl, replaceCurrentJobItemWith, replaceCurrentJobItemLabelWith];

  return {
    canGoBack,
    goBack,
    removeJobMenu,
    showDependantScreenMessage,
    hasOpenedScreenStartingWithUrl,
    hasOpenedScreenStartingWithUrlWithoutMe,
    hasOpenedScreenInAnyOperationStartingWithUrl,
    hasOpenedScreenInAnyOperationStartingWithUrlWithoutMe,
    getOpenedScreenInAnyOperationStartingWithUrl,
    moveToOpenedScreenInAnyOperationStartingWithUrl,
    replaceCurrentJobItemWith,
    replaceCurrentJobItemLabelWith,
    firstRun: isFirstRun(url),
    getFocusElementInStorage,
  };
}
  